import { injectable, inject } from 'inversify';

import { CalendarItem, CalendarItemColor } from '@vk-hr-tek/ui/Calendar';
import { CalendarDiff } from '@vk-hr-tek/ui/Calendar/types';
import { Filter } from '@vk-hr-tek/core/filter';
import { RoleService } from '@vk-hr-tek/core/http';
import { FormatService } from '@vk-hr-tek/core/format';
import { t } from '@vk-hr-tek/core/translations';

import {
  ScheduleEventParticipant as EventParticipant,
  ScheduleEventListItem as VacationListEntity,
  UnitScheduleItem,
  WorkScheduleCalendar,
  AbsenceIntersectionMonth,
  AbsenceIntersectionDay,
  VacationItem,
  UnitPlannedVacationsPercent,
} from '@app/gen/events';
import {
  CompanyVacationSchedule,
  CompanyVacationSchedulesListItem,
} from '@app/gen/vacations';

import { RANGE_PALETTE } from '../../../app/constants';
import { VacationsMapper } from '../general/vacations.mapper';
import { VacationsIntersection, VacationsIntersections } from '../../types';

@injectable()
export class VacationsListMapper {
  constructor(
    private transform: VacationsMapper,
    private format: FormatService,
    @inject(RoleService) private roleService: RoleService,
  ) {}

  private processRangeItem(
    vacations: VacationItem[],
    rangeColor: CalendarItemColor,
    imported: boolean = false,
    showCompensation: boolean = false,
  ) {
    return vacations.map(
      ({
        to_date,
        from_date,
        type_name,
        intersections_count,
        compensatory,
      }) => {
        const start = new Date(from_date);
        const end = new Date(to_date);

        return {
          start,
          end,
          ranges: [
            {
              start,
              end,
              color: rangeColor,
              palette: RANGE_PALETTE[rangeColor],
              compensation: compensatory && showCompensation,
              label: type_name,
              period: `с ${this.format.toDate(
                from_date,
              )} до ${this.format.toDate(to_date)}`,
              ...(!!intersections_count && {
                intersections: `Пересечения отсутствий: ${intersections_count}`,
              }),
              ...(imported && {
                importedText: 'На этот период уже оформлено отсутствие',
              }),
            },
          ],
        };
      },
    );
  }

  processScheduleTitle({
    year,
    full_name,
  }: Pick<CompanyVacationSchedule, 'year' | 'full_name'>) {
    return full_name || `График отпусков ${year} г.`;
  }

  processCompanies(companies: CompanyVacationSchedulesListItem[]) {
    return companies.map((company) => ({
      ...company,
      vacation_schedules: company.vacation_schedules.map((schedule) => ({
        ...schedule,
        title: this.processScheduleTitle(schedule),
      })),
    }));
  }

  processActiveParticipant(participant: EventParticipant) {
    return `${participant.type}${
      participant.data ? ` (${participant.data})` : ''
    }`;
  }

  processUnfilledVacationItem(vacation: VacationListEntity) {
    return {
      id: vacation.id,
      employee: {
        label: `${vacation.employee.personnel_number}, ${vacation.employee.second_name} ${vacation.employee.first_name}`,
        value: vacation.employee.id,
      },
    };
  }

  processListEntity(vacation: VacationListEntity, units?: UnitScheduleItem[]) {
    const calendarDiff = this.transform.processCalendar(vacation.calendar_diff);
    const unit = this.transform.processUnitName(units, vacation.unit_id);

    return {
      id: vacation.id,
      stage: this.transform.processStageList(vacation.status),
      upToMe: vacation.up_to_me,
      calendarDiff,
      side: vacation.active_participant,
      unit,
      company: 'company',
      employee: this.transform.processEmployee(vacation),
      deadline: vacation.deadline &&
        vacation.status !== 'finished' && {
          date: this.transform.processDeadline(vacation.deadline),
          color: 'text.primary',
        },
      actions: {
        id: vacation.id,
        active: vacation.up_to_me,
        user: this.transform.processUserActions(vacation, calendarDiff, unit),
        myEvent: vacation.my_event,
        favorite: vacation.favorite,
      },
      vacation: this.transform.processDescribeVacations(
        vacation.vacations,
        calendarDiff,
      ),
      consecutiveNumber: vacation.consecutive_number,
    };
  }

  processVacationsFilters(filters: Filter[], tags: string[] = []): Filter[] {
    const role = this.roleService.get();
    const isEmployee = role === 'employee';
    const onlyOneFilter =
      filters.filter(({ key }) => key !== 'actor').length === 1;

    const filterByRequestNumberTooltip = t([
      ...tags.map(
        (tag) => `vacations.list.filterByRequestNumberTooltip.${tag}`,
      ),
      'vacations.list.filterByRequestNumberTooltip.default',
    ]);

    return filters.map((filter) => {
      if (filter.type === 'multiple' && filter.key === 'unit_ids') {
        return {
          ...filter,
          //eslint-disable-next-line no-magic-numbers
          width: isEmployee ? 8 : 12,
        };
      }

      if (filter.type === 'bool') {
        return {
          ...filter,
          width: 12,
          fullWidth: false,
          labelPlacement: 'end',
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'employee_ids') {
        if (onlyOneFilter) {
          return {
            ...filter,
            width: 12,
          };
        }

        return {
          ...filter,
          twoLine: true,
          //eslint-disable-next-line no-magic-numbers
          width: isEmployee ? 4 : 6,
          minInputValueLength: 3,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'event_ids') {
        return {
          ...filter,
          tooltip: filterByRequestNumberTooltip,
        };
      }

      if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
        return {
          ...filter,
          width: 12,
        };
      }

      return {
        ...filter,
        //eslint-disable-next-line no-magic-numbers
        width: isEmployee ? 4 : 6,
      };
    });
  }

  processShowBottomInfo(filters: Filter[]): boolean {
    const role = this.roleService.get();

    if (role === 'company') {
      return true;
    }

    return !!filters.find((filter) => filter.key === 'actor');
  }

  processWorkScheduleCalendar(
    workSchedule: WorkScheduleCalendar,
  ): CalendarDiff {
    return (workSchedule.dates || []).reduce((acc: CalendarDiff, curr) => {
      acc[curr.date] = {
        workday: curr.is_workday,
        holiday: curr.is_holiday,
      };
      return acc;
    }, {});
  }

  processCalendarDiff(vacation: VacationListEntity): CalendarDiff {
    if (vacation.work_schedule) {
      return this.processWorkScheduleCalendar(vacation.work_schedule);
    }

    return this.transform.processCalendar(vacation.calendar_diff);
  }

  processCalendarEntity(
    vacation: VacationListEntity[],
    units?: UnitScheduleItem[],
    percents?: UnitPlannedVacationsPercent[],
  ): CalendarItem[] {
    const unitListEntity = vacation.reduce(
      (acc: Record<string, VacationListEntity[]>, curr) => {
        const unitId = curr?.unit_id ?? 'empty';

        if (!acc[unitId]) acc[unitId] = [];

        acc[unitId].push(curr);

        return acc;
      },
      {},
    );

    const percentsListEntity =
      percents &&
      percents.reduce(
        (acc: Record<string, UnitPlannedVacationsPercent>, curr) => {
          const unitId = curr?.unit_id ?? 'empty';

          if (!acc[unitId]) acc[unitId] = curr;

          return acc;
        },
        {},
      );

    const { empty: emptyList, ...unitList } = unitListEntity;
    const result: CalendarItem[] = [];

    for (const id in unitList) {
      result.push(
        this.processCalendarGroup(
          unitList[id],
          units,
          percentsListEntity?.[id],
        ),
      );

      unitList[id].map((eventListItem) => {
        result.push(this.processCalendarItems(eventListItem, units));
      });
    }

    if (emptyList?.length > 0) {
      result.push(
        this.processCalendarGroup(
          emptyList,
          undefined,
          percentsListEntity?.['empty'],
        ),
      );

      emptyList.forEach((eventListItem) => {
        result.push(this.processCalendarItems(eventListItem, units));
      });
    }

    return result;
  }

  processCalendarGroup(
    vacations: VacationListEntity[],
    units?: UnitScheduleItem[],
    percentEntity?: UnitPlannedVacationsPercent,
  ): CalendarItem {
    const groupUnitId = vacations[0]?.unit_id;
    const unit = units?.find(({ id }) => groupUnitId && id === groupUnitId);

    return {
      type: 'group',
      selectableIds: vacations
        .filter(({ up_to_me }) => up_to_me)
        .map(({ id }) => id),
      name: unit?.name,
      id: groupUnitId || 'empty',
      structure: this.transform.processGroupStructure(units, groupUnitId),
      statuses: this.transform.processGroupStatuses(vacations),
      supervisor: this.transform.processGroupSupervisor(unit),
      vacationPercents: this.transform.processSchedulePercents(percentEntity),
    };
  }

  processCalendarItems(
    vacation: VacationListEntity,
    units?: UnitScheduleItem[],
  ): CalendarItem {
    let color: CalendarItemColor = 'green';

    if (vacation.status === 'canceled') color = 'red';
    else if (vacation.status === 'accept') color = 'purple';
    else if (vacation.status === 'finished') color = 'green';
    else if (vacation.status === 'onec_sync') color = 'orange';
    else if (vacation.status === 'new') color = 'blue';

    const calendarDiff = this.processCalendarDiff(vacation);
    const unit = this.transform.processUnitName(units, vacation.unit_id);
    const showCompensation =
      vacation.my_event || this.roleService.get() === 'company';

    return {
      id: vacation.id,
      type: 'item',
      upToMe: vacation.up_to_me,
      calendarDiff,
      stage: this.transform.processStage(vacation.status),
      name: this.transform.processInitialName(vacation.employee),
      activeParticipant: vacation.active_participant
        ? this.processActiveParticipant(vacation.active_participant)
        : '',
      personalNumber: vacation.employee.personnel_number,
      position: vacation.employee.position,
      color,
      ranges: [
        ...this.processRangeItem(
          vacation.vacations,
          color,
          false,
          showCompensation,
        ),
        ...this.processRangeItem(vacation.imported_absences, 'gray', true),
      ],
      deadline:
        vacation.deadline && this.transform.processDeadline(vacation.deadline),
      actions: this.transform.processUserActions(vacation, calendarDiff, unit),
      favorite: vacation.favorite,
      myEvent: vacation.my_event,
    };
  }

  processDayIntersections(intersections: AbsenceIntersectionDay[]) {
    return intersections.reduce<Record<number, VacationsIntersection>>(
      (acc, intersection) => {
        const day = new Date(intersection.date).getDate();

        acc[day] = {
          total: intersection.total,
          quantity: intersection.fraction,
        };

        return acc;
      },
      {},
    );
  }

  processMonthIntersections(intersections: AbsenceIntersectionMonth[]) {
    return intersections.reduce<VacationsIntersections>((acc, intersection) => {
      acc[intersection.month] = {
        total: intersection.total,
        quantity: intersection.fraction,
        days: this.processDayIntersections(intersection.days),
      };

      return acc;
    }, {});
  }
}
